import React, { useState, useCallback, ChangeEvent } from 'react';
import { Upload } from 'lucide-react';
import { Card } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../../config/api.config';
import { FileUploadProps } from '../../../types/files';
import { apiService } from '../../../services/apiService';

interface UploadUrlResponse {
    uploadUrl: string;
    objectName: string;
    dataSourceId: string;
}

interface UploadCompleteResponse {
    success: boolean;
    message?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
                                                   onUploadSuccess,
                                                   acceptedFileTypes = ".txt,.pdf,.docx,.md,.csv,.json,.xlsx,.xls,.pptx,.ppt",
                                                   maxFileSize = 50 * 1024 * 1024,
                                                   currentFolderId = null,
                                                   fetchFiles,
                                                   fetchFolders,
                                                   currentPage,
                                                   itemsPerPage,
                                                   searchTerm,
                                                   source,
                                                   sort,
                                                   rootFolderId
                                               }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number>(0);

    const validateFile = (file: File): string | null => {
        if (maxFileSize && file.size > maxFileSize) {
            return `File size exceeds ${maxFileSize / (1024 * 1024)}MB limit`;
        }
        if (acceptedFileTypes !== '*') {
            const fileType = file.type || '';
            const extension = file.name.split('.').pop()?.toLowerCase() || '';
            const acceptedTypes = acceptedFileTypes.split(',').map(type => type.trim().toLowerCase());

            const isAccepted = acceptedTypes.some(type => {
                const cleanType = type.startsWith('.') ? type.substring(1) : type;
                return fileType.includes(cleanType) || extension === cleanType;
            });

            if (!isAccepted) {
                return 'File type not accepted';
            }
        }
        return null;
    };

    const uploadFile = async (file: File) => {
        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsUploading(true);
        setError(null);
        setUploadProgress(0);

        try {
            // Step 1: Get signed URL
            const uploadUrlEndpoint = `${API_ENDPOINTS.FILES.UPLOAD_URL}?filename=${encodeURIComponent(file.name)}${currentFolderId ? `&folderId=${currentFolderId}` : ''}`;
            const { uploadUrl, objectName, dataSourceId } = await apiService.get<UploadUrlResponse>(uploadUrlEndpoint);

            // Step 2: Upload to storage
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', uploadUrl);
            xhr.setRequestHeader('Content-Type', file.type);
            xhr.setRequestHeader('Content-Length', file.size.toString());

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(progress);
                }
            };

            await new Promise<void>((resolve, reject) => {
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve();
                    } else {
                        reject(new Error(`Upload failed with status ${xhr.status}`));
                    }
                };
                xhr.onerror = () => reject(new Error('Network error during upload'));
                xhr.send(file);
            });

            // Step 3: Complete upload
            await apiService.post<UploadCompleteResponse>(
                API_ENDPOINTS.FILES.UPLOAD_COMPLETE,
                {
                    dataSourceId,
                    objectName,
                    size: file.size,
                    mimeType: file.type,
                    folderId: currentFolderId
                }
            );

            setUploadProgress(100);

            // Refresh the folder contents
            await Promise.all([
                fetchFolders(currentFolderId),
                fetchFiles({
                    currentPage,
                    itemsPerPage,
                    searchTerm,
                    source,
                    sort,
                    currentFolderId,
                    rootFolderId
                })
            ]);

            await onUploadSuccess();

            setTimeout(() => {
                setUploadProgress(0);
            }, 1000);

        } catch (err) {
            console.error('Upload error:', err);
            setError(err instanceof Error ? err.message : 'Failed to upload file');
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileUpload = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            await uploadFile(file);
        } finally {
            if (event.target) {
                event.target.value = '';
            }
        }
    }, [currentFolderId]);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const file = e.dataTransfer.files[0];
        if (!file) return;

        await uploadFile(file);
    }, [currentFolderId]);

    return (
        <Card className="border-2 border-dashed hover-shadow">
            <Card.Body className="p-4">
                <div
                    className="d-flex flex-column align-items-center justify-content-center w-100"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <label
                        className="d-flex flex-column align-items-center justify-content-center w-100 cursor-pointer"
                        style={{ cursor: 'pointer' }}
                    >
                        <Upload className={`mb-3 ${isUploading ? 'text-primary' : 'text-muted'}`} size={48} />
                        <div className="text-center">
                            <p className="text-muted mb-1 fw-semibold">
                                Click to upload or drag and drop
                            </p>
                            <small className="text-muted">
                                Supported formats: {acceptedFileTypes}
                            </small>
                            <input
                                type="file"
                                className="d-none"
                                onChange={handleFileUpload}
                                disabled={isUploading}
                                accept={acceptedFileTypes}
                            />
                        </div>
                    </label>

                    {isUploading && (
                        <div className="w-100 mt-3" style={{ maxWidth: '300px' }}>
                            <div className="progress mb-2" style={{ height: '6px' }}>
                                <div
                                    className="progress-bar"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                            <p className="text-primary text-center small">
                                Uploading... {uploadProgress}%
                            </p>
                        </div>
                    )}

                    {error && (
                        <div className="text-danger small mt-2">
                            ⚠️ {error}
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default FileUpload;